import { defineComponent, Transition } from "vue";
import { VueContext, Window } from "../../types";
import { createAppWithRootComponent, VueProp } from "../core/vueHelpers";
import { Classes } from "./classes";
import DesktopMenu from "./desktop/desktopMenu";
import { createFocusHandler } from "./desktop/focusHandler";
import { ModuleButtonData, ModuleMenuScreenCategoryData, ModuleMenuScreenData, PortalButtonData } from "./index";
import { CachedPortalData, MainMenuCache } from "./mainMenuCache";

export interface MainMenuContext extends VueContext
{
    menuData: MainMenuCache;
    window: Window;
    newWindowOpened: () => void;
}

const rootComponent = defineComponent(
    {
        name: "mainMenu",
        props:
        {
            isMobile: VueProp(Boolean),
            menuData: VueProp<MainMenuCache>(),
            window: VueProp<Window>(),
            newWindowOpened: VueProp<() => void>(Function),
        },
        data()
        {
            const initialData =
            {
                isOpen: false,
                top: 0,
                selectedPortal: { selectedModuleID: null, moduleButtons: [], modules: {} } as CachedPortalData,
                moduleCategories: [] as ModuleMenuScreenCategoryData[],
            };
            return initialData;
        },
        computed:
        {
            focusHandler()
            {
                return createFocusHandler(this.menuData);
            }
        },
        created()
        {
            const vm = this;

            vm.menuData.getPortal(vm.menuData.selectedPortalCode).then(function (portal)
            {
                if (portal.selectedModuleID === null)
                {
                    return;
                }

                vm.menuData.getModuleCategories(vm.menuData.selectedPortalCode, portal.selectedModuleID).then(function (categories)
                {
                    vm.selectedPortal = portal;
                    vm.moduleCategories = categories;
                });
            });
        },
        render()
        {
            const vm = this;

            const menu = vm.isOpen
                ? (this.isMobile ? getMobileMenu() : getDesktopMenu())
                : null;

            return (
                <Transition
                    name={Classes.menu}
                    enter-from-class={Classes.menuClosed} enter-active-class={Classes.menuTransitioning} enter-to-class={Classes.menuOpen}
                    leave-from-class={Classes.menuOpen} leave-active-class={Classes.menuTransitioning} leave-to-class={Classes.menuClosed}
                    onAfterEnter={() =>
                    {
                        this.focusHandler.focusFirstItem();
                        $(`.${Classes.moduleButtonSelected}`).scrollintoview({ duration: 0 });
                    }}
                    // Normally, the default slot's contents can be set inline, such as: <Transition>{menu}</Transition>.
                    // However, when the jsx transform plugin has optimizations turned on, passing a null value
                    // in the slot causes an error.  Setting it manually works around this.
                    v-slots={{
                        default: () => menu
                    }}
                />
            );

            function getDesktopMenu()
            {
                return (
                    <DesktopMenu
                        selectedPortalCode={vm.menuData.selectedPortalCode}
                        portalButtonDatas={vm.menuData.portalButtons}
                        selectedPortal={vm.selectedPortal}
                        moduleMenuScreenCategoryDatas={vm.moduleCategories}
                        focusHandler={vm.focusHandler}
                        style={"top:" + vm.top + "px"}
                        onSelectPortal={(portalButtonData: PortalButtonData) => vm.__selectPortal(portalButtonData)}
                        onSelectModule={(moduleButtonData: ModuleButtonData) => vm.__selectModule(moduleButtonData)}
                        onSelectScreen={(moduleMenuScreenData: ModuleMenuScreenData, newWindow: boolean) => onSelectScreen(vm.window, moduleMenuScreenData, newWindow, vm.newWindowOpened)}
                    />
                );
            }

            function getMobileMenu()
            {
                // TODO: [PR 4380821] Mobile Menu
                return <span />;
            }
        },
        methods:
        {
            toggleOpen(toggle?: boolean): void
            {
                this.isOpen = toggle === undefined ? !this.isOpen : toggle;
            },
            setTopPosition(top: number): void
            {
                this.top = top;
            },

            __selectPortal(portalButtonData: PortalButtonData): void
            {
                const newPortalCode = portalButtonData.portalCode;
                if (newPortalCode === null)
                {
                    return;
                }

                const vm = this;
                const cache = vm.menuData;

                getFromCache(cache, newPortalCode, null, function (portal, categories)
                {
                    cache.selectedPortalCode = newPortalCode;
                    vm.__setSelectedValues(portal, categories);
                });
            },
            __selectModule(moduleButtonData: ModuleButtonData): void
            {
                const vm = this;
                const cache = vm.menuData;
                const selectedPortalCode = cache.selectedPortalCode;

                getFromCache(cache, selectedPortalCode, moduleButtonData.moduleID, function (portal, categories)
                {
                    portal.selectedModuleID = moduleButtonData.moduleID;
                    vm.__setSelectedValues(portal, categories);
                });
            },
            __setSelectedValues(portal: CachedPortalData, categories: ModuleMenuScreenCategoryData[]): void
            {
                this.selectedPortal = portal;
                this.moduleCategories = categories;
            }
        }
    }
);

export const mainMenuConstructor =
{
    buildVue: function (context: MainMenuContext)
    {
        return createAppWithRootComponent(rootComponent,
        {
            isMobile: context.isMobile,
            window: context.window,
            menuData: context.menuData,
            newWindowOpened: context.newWindowOpened,
        })
        .mount(context.container);
    }
};

export const MainMenu = mainMenuConstructor;

function getFromCache(cache: MainMenuCache, portalCode: string, moduleID: number | null, callback: (portal: CachedPortalData, categories: ModuleMenuScreenCategoryData[]) => void)
{
    cache.getPortal(portalCode).then(function (portal)
    {
        if (portal.selectedModuleID === null)
        {
            return;
        }

        const newSelectedModuleID = moduleID !== null ? moduleID : portal.selectedModuleID;
        cache.getModuleCategories(portalCode, newSelectedModuleID).then(function (categories)
        {
            callback(portal, categories);
        });
    });
}

function onSelectScreen(window: Window, moduleMenuScreenData: ModuleMenuScreenData, newWindow: boolean, newWindowOpened: Function): void
{
    const target = newWindow ? "popout" : moduleMenuScreenData.target;
    window.loadContent(target, moduleMenuScreenData.module, moduleMenuScreenData.object, moduleMenuScreenData.screen, null, moduleMenuScreenData.data, null, null, null, true, null, null, null);
    if (newWindow)
    {
        newWindowOpened();
    }
};
